import type { ReactNode } from "react";
import { SheetDescription, SheetHeader, SheetTitle } from "../../../components/ui/sheet";

interface DrilldownSheetHeaderProps {
  /** The chart/section the drilldown came from, e.g. "Trend on Actual & Forecast
   * by Vulnerabilities". */
  title: string;
  /** The clicked slice, rendered muted after a " : " separator (e.g. "On Track").
   * Omit for drilldowns that aren't scoped to a named slice. */
  slice?: ReactNode;
  /** Second line under the title, e.g. "Month: Jul-2026". */
  subtitle?: ReactNode;
}

/** Shared header for every IT Compliance drilldown drawer (Overview trend, VM/CM
 * chart slices, Top Risk).
 *
 * It exists so the six drawers can't drift apart on type scale: `SheetTitle`
 * ships no font-size of its own, so each drawer was inheriting the document
 * default and rendering oversized. The compact `text-sm` title + `text-xs`
 * subtitle here match the module's other drawers (PluginDetailDrawer) and
 * ServerDetailsModal, and mirror g360's drilldown header, which pairs a bold
 * title with a colour-muted (not smaller) slice suffix. */
export function DrilldownSheetHeader({ title, slice, subtitle }: DrilldownSheetHeaderProps) {
  return (
    <SheetHeader className="gap-1 border-b border-border px-3 py-2">
      <SheetTitle className="text-sm font-semibold text-foreground">
        {title}
        {slice != null && slice !== "" && <span className="text-muted-foreground"> : {slice}</span>}
      </SheetTitle>
      {subtitle != null && subtitle !== "" && (
        <SheetDescription className="text-xs text-muted-foreground">{subtitle}</SheetDescription>
      )}
    </SheetHeader>
  );
}
